import { Box, CircularProgress, Typography } from '@mui/material';

interface CircularProgressWithLabelProps {
  value: number;
  size?: number;
  labelPosition?: 'inside' | 'left' | 'right';
  /** When true, renders a spinning ring while still showing the progress label from `value`. */
  indeterminate?: boolean;
}

/**
 * Circular progress with an optional percent label (inside the ring by default).
 * Use `indeterminate` to animate the ring while keeping the label visible.
 */
export function CircularProgressWithLabel({
  value,
  size = 40,
  labelPosition = 'inside',
  indeterminate = false,
}: CircularProgressWithLabelProps) {
  // Omit percent at 0; show at least 1% for any positive sub-1 progress so users see movement.
  const displayPercent = value <= 0 ? null : value < 1 ? 1 : Math.round(value);
  const labelText = displayPercent !== null ? `${displayPercent}%` : null;
  const progressRing = (
    <CircularProgress
      variant={indeterminate ? 'indeterminate' : 'determinate'}
      value={indeterminate ? undefined : value}
      size={size}
      thickness={4}
    />
  );

  if (labelPosition === 'left') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {labelText && (
          <Typography
            variant="caption"
            component="div"
            color="text.secondary"
            sx={{ fontSize: '0.75rem', color: 'inherit' }}
          >
            {labelText}
          </Typography>
        )}
        {progressRing}
      </Box>
    );
  }

  if (labelPosition === 'right') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {progressRing}
        {labelText && (
          <Typography
            variant="caption"
            component="div"
            color="text.secondary"
            sx={{ fontSize: '0.75rem' }}
          >
            {labelText}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress
        variant={indeterminate ? 'indeterminate' : 'determinate'}
        value={indeterminate ? undefined : value}
        size={size * 1.1}
        thickness={3}
      />
      {labelText && (
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="caption"
            component="div"
            color="text.secondary"
            sx={{ fontSize: '0.7rem' }}
          >
            {labelText}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
